/**
 * User Assignments Table
 *
 * Server-paged view of user assignments (user_course_assignments view).
 * Read-only — enrollment status, assignment status, and dates.
 */

import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CourseEnrollment } from '../../apis/api-client';
import { userAssignmentsClient } from '../../apis/client-instances';
import PagedTable, { PagedTableColumn } from '../common/PagedTable';
import { alert } from '../../hooks/function';
import { usePagedList } from '../../hooks/usePagedList';
import {
  formatDate,
  formatDateTime,
  getAssignmentStatusColor,
  getAssignmentStatusLabel,
  getEnrollmentStatusColor,
  getEnrollmentStatusLabel,
} from '../../utils/roleHelpers';
import {
  Box,
  Chip,
  Link,
  Typography,
} from '../ui';

interface UserAssignmentsTableProps {
  courseId?: number | null;
  workspaceId?: number | null;
}

export default function UserAssignmentsTable({ courseId, workspaceId }: UserAssignmentsTableProps) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const paged = usePagedList<CourseEnrollment>({
    fetcher: useCallback(
      async ({ page, pageSize, search }) => {
        const result = await userAssignmentsClient.searchAllUserAssignments(
          workspaceId ?? undefined,
          undefined, // assignmentStatus
          undefined, // enrollmentStatus
          courseId ?? undefined,
          undefined, // userId
          search || undefined,
          page,
          pageSize
        );
        return {
          items: result.items ?? [],
          totalCount: result.totalCount ?? 0,
          page: result.page ?? page,
          pageSize: result.pageSize ?? pageSize,
        };
      },
      [workspaceId, courseId]
    ),
    deps: [workspaceId ?? null, courseId ?? null],
    onError: () => alert(enqueueSnackbar, 'Failed to load user assignments', 'error'),
  });

  const columns: PagedTableColumn<CourseEnrollment>[] = [
    {
      key: 'student',
      label: 'Student',
      render: (a) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {a.firstName} {a.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {a.email}
          </Typography>
        </Box>
      ),
    },
    ...(!courseId
      ? [{
          key: 'course',
          label: 'Course',
          render: (a: CourseEnrollment) => (
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate(`/course/${a.courseId}`)}
              sx={{ textAlign: 'left', fontWeight: 500 }}
            >
              {a.courseTitle || `Course #${a.courseId}`}
            </Link>
          ),
        }]
      : []),
    {
      key: 'startDate',
      label: 'Start Date',
      render: (a) => <Typography variant="body2">{formatDate(a.startDate)}</Typography>,
    },
    {
      key: 'deadline',
      label: 'Deadline',
      render: (a) => (
        <Typography variant="body2" fontWeight={500}>
          {formatDate(a.deadline)}
        </Typography>
      ),
    },
    {
      key: 'enrollmentStatus',
      label: 'Enrollment Status',
      render: (a) => (
        <Chip
          label={getEnrollmentStatusLabel(a.enrollmentStatus)}
          color={getEnrollmentStatusColor(a.enrollmentStatus)}
          size="small"
        />
      ),
    },
    {
      key: 'assignmentStatus',
      label: 'Assignment Status',
      render: (a) =>
        a.assignmentStatus ? (
          <Chip
            label={getAssignmentStatusLabel(a.assignmentStatus)}
            color={getAssignmentStatusColor(a.assignmentStatus)}
            size="small"
          />
        ) : null,
    },
    {
      key: 'lastAccessed',
      label: 'Last Accessed',
      render: (a) => <Typography variant="body2">{formatDateTime(a.lastAccessedAt)}</Typography>,
    },
  ];

  return (
    <PagedTable<CourseEnrollment>
      columns={columns}
      rows={paged.items}
      totalCount={paged.totalCount}
      page={paged.page}
      pageSize={paged.pageSize}
      loading={paged.loading}
      rowKey={(a) => `${a.userId}-${a.courseId}-${a.enrollmentId ?? 'none'}`}
      onPageChange={paged.setPage}
      onPageSizeChange={paged.setPageSize}
      search={paged.search}
      searchPlaceholder="Search by student or course..."
      onSearchChange={paged.setSearch}
      emptyMessage="No student assignments yet"
    />
  );
}
